import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, useCallback, useRef, useMemo } from "react";
import { useGetRecordLogQuery } from "../quick-entry/recordApiSlice";
import { formatUtcDatetime } from "../../../helpers/dateUtils";
import { useGetTeachersPaginatedQuery } from "../staff/staffApiSlice";
import {
  useGetClassesPaginatedQuery,
  useGetClassroomStudentsPaginatedQuery,
} from "../classroom/classApiSlice";
import { useGetSubjectsPaginatedQuery } from "../subjects/subjectsApiSlice";
import { useGetAssessmentsQuery } from "../settings/assessments/assessmentsApiSlice";
import { useGetAllStudentsQuery } from "../students/studentApiSlice";
import { useGetSubSectionQuery } from "../settings/sections/sectionsApiSlice";

const PER_PAGE = 10;

function formatRawScores(raw) {
  if (!raw?.scores?.length) return null;
  return raw.scores
    .map((s) => `${s.obtained ?? 0}/${s.obtainable ?? 0}`)
    .join(", ");
}

function formatDate(iso) {
  return formatUtcDatetime(iso);
}

export default function ScoreLog() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(1);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [teacherInputValue, setTeacherInputValue] = useState("");
  const [classroomInputValue, setClassroomInputValue] = useState("");
  const [subjectInputValue, setSubjectInputValue] = useState("");
  const [studentInputValue, setStudentInputValue] = useState("");

  const [teacherSearch, setTeacherSearch] = useState("");
  const [classroomSearch, setClassroomSearch] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");

  const debounceRef = useRef(null);
  const classroomDebounceRef = useRef(null);
  const subjectDebounceRef = useRef(null);

  const { data: teacherData, isFetching: teachersFetching } = useGetTeachersPaginatedQuery({
    per_page: 10,
    q: teacherSearch,
  });
  const { data: classroomData, isFetching: classroomsFetching } = useGetClassesPaginatedQuery({
    page: 1,
    per_page: 20,
    q: classroomSearch,
  });
  const { data: subjectData, isFetching: subjectsFetching } = useGetSubjectsPaginatedQuery({
    page: 1,
    per_page: 20,
    q: subjectSearch,
  });
  const { data: assessmentsData, isFetching: assessmentsFetching } = useGetAssessmentsQuery("all");
  const { data: subsections } = useGetSubSectionQuery("all");
  const { data: allStudents, isFetching: allStudentsFetching } = useGetAllStudentsQuery(undefined, {
    skip: Boolean(selectedClassroom?.id),
  });
  const { data: classroomStudentsData, isFetching: classroomStudentsFetching } =
    useGetClassroomStudentsPaginatedQuery(
      {
        classroom_id: selectedClassroom?.id,
        page: 1,
        per_page: 200,
        q: studentInputValue,
      },
      { skip: !selectedClassroom?.id }
    );

  const teachers = teacherData?.teachers ?? [];
  const classrooms = classroomData?.classrooms ?? [];
  const subjects = subjectData?.subjects ?? [];
  const assessments = assessmentsData ?? [];
  const studentsFetching = selectedClassroom?.id
    ? classroomStudentsFetching
    : allStudentsFetching;

  const subsectionById = useMemo(() => {
    const map = new Map();
    (subsections || []).forEach((subsection) => {
      map.set(subsection.id, subsection.name);
    });
    return map;
  }, [subsections]);

  const getAssessmentLabel = useCallback(
    (assessment) => {
      if (!assessment) return "";
      const subsectionName = subsectionById.get(assessment.subsection_id);
      return subsectionName
        ? `${assessment.name} • ${subsectionName}`
        : assessment.name || "";
    },
    [subsectionById]
  );

  const filteredStudentOptions = useMemo(() => {
    const allStudentsList = allStudents ?? [];
    const classroomStudentsList = classroomStudentsData?.students ?? [];
    return selectedClassroom?.id ? classroomStudentsList : allStudentsList;
  }, [selectedClassroom?.id, classroomStudentsData?.students, allStudents]);

  const handleTeacherInputChange = useCallback((_, value) => {
    setTeacherInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setTeacherSearch(value);
    }, 300);
  }, []);

  const handleClassroomInputChange = useCallback((_, value) => {
    setClassroomInputValue(value);
    if (classroomDebounceRef.current) clearTimeout(classroomDebounceRef.current);
    classroomDebounceRef.current = setTimeout(() => {
      setClassroomSearch(value);
    }, 300);
  }, []);

  const handleSubjectInputChange = useCallback((_, value) => {
    setSubjectInputValue(value);
    if (subjectDebounceRef.current) clearTimeout(subjectDebounceRef.current);
    subjectDebounceRef.current = setTimeout(() => {
      setSubjectSearch(value);
    }, 300);
  }, []);

  const { data, isLoading, isFetching } = useGetRecordLogQuery({
    page,
    per_page: PER_PAGE,
    recorded_by: selectedTeacher?.user_id ?? undefined,
    classroom_id: selectedClassroom?.id ?? undefined,
    subject_id: selectedSubject?.id ?? undefined,
    assessment_id: selectedAssessment?.id ?? undefined,
    student_id: selectedStudent?.id ?? undefined,
  });

  const records = data?.records ?? [];
  const totalPages = data?.pages ?? 1;

  const handleTeacherChange = (_, value) => {
    setSelectedTeacher(value);
    setPage(1);
  };

  const handleClassroomChange = (_, value) => {
    setSelectedClassroom(value);
    setSelectedStudent(null);
    setStudentInputValue("");
    setPage(1);
  };

  const handleSubjectChange = (_, value) => {
    setSelectedSubject(value);
    setPage(1);
  };

  const handleAssessmentChange = (_, value) => {
    setSelectedAssessment(value);
    setPage(1);
  };

  const handleStudentChange = (_, value) => {
    setSelectedStudent(value);
    setPage(1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Score Entry Log
      </Typography>

      <Box
        sx={{
          mb: 2,
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(220px, 1fr))", lg: "repeat(5, minmax(180px, 1fr))" },
        }}
      >
        <Autocomplete
          options={teachers}
          getOptionLabel={(t) => `${t.first_name} ${t.last_name}`}
          value={selectedTeacher}
          onChange={handleTeacherChange}
          inputValue={teacherInputValue}
          onInputChange={handleTeacherInputChange}
          isOptionEqualToValue={(opt, val) => opt.user_id === val.user_id}
          filterOptions={(x) => x}
          loading={teachersFetching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Teacher"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {teachersFetching && <CircularProgress color="inherit" size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          clearOnEscape
        />

        <Autocomplete
          options={classrooms}
          getOptionLabel={(c) => c?.name || ""}
          value={selectedClassroom}
          onChange={handleClassroomChange}
          inputValue={classroomInputValue}
          onInputChange={handleClassroomInputChange}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          filterOptions={(x) => x}
          loading={classroomsFetching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Class"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {classroomsFetching && <CircularProgress color="inherit" size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          clearOnEscape
        />

        <Autocomplete
          options={subjects}
          getOptionLabel={(s) => s?.name || ""}
          value={selectedSubject}
          onChange={handleSubjectChange}
          inputValue={subjectInputValue}
          onInputChange={handleSubjectInputChange}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          filterOptions={(x) => x}
          loading={subjectsFetching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Subject"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {subjectsFetching && <CircularProgress color="inherit" size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          clearOnEscape
        />

        <Autocomplete
          options={assessments}
          getOptionLabel={getAssessmentLabel}
          value={selectedAssessment}
          onChange={handleAssessmentChange}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {getAssessmentLabel(option)}
            </li>
          )}
          loading={assessmentsFetching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assessment Type"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {assessmentsFetching && <CircularProgress color="inherit" size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          clearOnEscape
        />

        <Autocomplete
          options={filteredStudentOptions}
          getOptionLabel={(s) => `${s.first_name || ""} ${s.last_name || ""}`.trim()}
          value={selectedStudent}
          onChange={handleStudentChange}
          inputValue={studentInputValue}
          onInputChange={(_, value) => setStudentInputValue(value)}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          loading={studentsFetching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Student"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {studentsFetching && <CircularProgress color="inherit" size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          clearOnEscape
        />
      </Box>

      {(isLoading || isFetching) && <LinearProgress sx={{ mb: 1 }} />}

      {isMobile ? (
        <Box>
          {records.length === 0 && !isLoading && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
              No records found for the current term.
            </Paper>
          )}
          {records.map((rec) => {
            const rawScores = formatRawScores(rec.raw);
            const recordedAt = rec.raw?.recorded_at
              ? formatDate(rec.raw.recorded_at)
              : formatDate(rec.updated_at);
            const obtainable = rec.assessment?.obtainable_score;
            return (
              <Paper key={rec.id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {rec.student ? `${rec.student.first_name} ${rec.student.last_name}` : "—"}
                </Typography>
                {rec.student?.reg_number && (
                  <Typography variant="caption" color="text.secondary">{rec.student.reg_number}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">Teacher: {rec.recorded_by_name || "Direct entry"}</Typography>
                <Typography variant="body2" color="text.secondary">Class: {rec.classroom?.name ?? "—"}</Typography>
                <Typography variant="body2" color="text.secondary">Subject: {rec.subject?.name ?? "—"}</Typography>
                <Typography variant="body2" color="text.secondary">Assessment: {rec.assessment?.name ?? "—"}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Chip
                    label={`${rec.score ?? 0} / ${obtainable ?? "?"}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ color: "primary.safeOnLight", borderColor: "primary.safeOnLight" }}
                  />
                  <Typography variant="caption" color="text.secondary">{recordedAt}</Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Raw: {rawScores || "—"}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                <TableCell>Teacher</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Assessment</TableCell>
                <TableCell align="center">Score</TableCell>
                <TableCell>Raw Sub-scores</TableCell>
                <TableCell>Recorded At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No records found for the current term.
                  </TableCell>
                </TableRow>
              )}
              {records.map((rec) => {
                const rawScores = formatRawScores(rec.raw);
                const recordedAt = rec.raw?.recorded_at
                  ? formatDate(rec.raw.recorded_at)
                  : formatDate(rec.updated_at);
                const obtainable = rec.assessment?.obtainable_score;

                return (
                  <TableRow key={rec.id} hover>
                    <TableCell>{rec.recorded_by_name ?? <Typography variant="body2" color="text.secondary">Direct entry</Typography>}</TableCell>
                    <TableCell>
                      {rec.student
                        ? `${rec.student.first_name} ${rec.student.last_name}`
                        : "—"}
                      {rec.student?.reg_number && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {rec.student.reg_number}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{rec.classroom?.name ?? "—"}</TableCell>
                    <TableCell>{rec.subject?.name ?? "—"}</TableCell>
                    <TableCell>{rec.assessment?.name ?? "—"}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${rec.score ?? 0} / ${obtainable ?? "?"}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ color: "primary.safeOnLight", borderColor: "primary.safeOnLight" }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {rawScores ?? <Typography variant="body2" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{recordedAt}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {data?.total != null
            ? `Page ${data.page} of ${data.pages} (${data.total} record${data.total !== 1 ? "s" : ""})`
            : ""}
        </Typography>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
            size={isMobile ? "small" : "medium"}
          />
        )}
      </Box>
    </Box>
  );
}
